import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskText, EASE } from "./ui/motion";
import { Panel, Parallax } from "../journey/Panel";
import Meta from "../image/CERTIFICATE_LANDING_PAGE~GNFRPR5QHT4W.jpeg";
import Udemy from "../image/UDEMY REACT.jpg";
import Nextjs from "../image/nextjs.jpg";
import Git from "../image/Git and Github.png";

/* Oldest → newest, so the journey reads left-to-right (and
   top-to-bottom on mobile) as the road actually unfolded. */
const TIMELINE = [
  {
    period: "2010 — 2016",
    role: "Secondary School Education",
    org: "Naousa, Greece",
    type: "Education",
    desc: "Lower and upper secondary school in Naousa, where I narrowed in on the science track that pointed me toward university.",
  },
  {
    period: "2016 — 2022",
    role: "MSc, Information & Electronic Engineering",
    org: "International Hellenic University",
    type: "Education",
    desc: "A five-year integrated master's covering the breadth of information and electronic engineering — studied at the Sindos campus in Thessaloniki.",
  },
  {
    period: "May 2023 — Mar 2024",
    role: "IT Support & Systems Admin",
    org: "Greek Army — Research & Informatics Technology Office",
    type: "Work",
    desc: "Spent my compulsory military service in the Research & Technology Office, keeping systems running as an IT support specialist and systems administrator.",
  },
  {
    period: "Jan 2025 — Mar 2025",
    role: "ERP Developer",
    org: "CFORCE",
    type: "Work",
    desc: "Worked on ERP systems with Softone and Plan B invoicing, managed and tuned SQL databases, wrote some Pascal, and handled customer support along with the usual hardware and software troubleshooting.",
  },
  {
    period: "Mar 2025 — Now",
    role: "Front-End Engineer",
    org: "StellarBlue",
    type: "Work",
    desc: "Working on an ETRM platform, building out the front-end with Vue.js and Bootstrap on top of a C# backend.",
  },
];

const CERTS = [
  { image: Meta, title: "Meta Certification", tag: "Meta" },
  { image: Udemy, title: "React — Udemy", tag: "Udemy" },
  { image: Git, title: "Git & GitHub", tag: "Version Control" },
  { image: Nextjs, title: "Next.js", tag: "Framework" },
];

function SectionHeader() {
  return (
    <div className="flex items-center gap-4 mb-12">
      <span className="eyebrow">(03)</span>
      <span className="h-px w-12 bg-acid" />
      <span className="eyebrow">Career &amp; Education</span>
    </div>
  );
}

function ItemBody({ item }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-acid">
          {item.period}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper-dim border border-paper/20 rounded-full px-2 py-0.5">
          {item.type}
        </span>
      </div>
      <h3 className="display text-3xl sm:text-4xl text-paper leading-tight">
        {item.role}
      </h3>
      <p className="text-sm font-mono text-paper-dim">{item.org}</p>
      <p className="text-base text-paper/70 leading-relaxed max-w-2xl mt-1">
        {item.desc}
      </p>
    </div>
  );
}

/* Vertical (mobile) timeline item — dot on a left rail */
function TimelineItem({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12% 0px" }}
      transition={{ duration: 0.8, delay: index * 0.05, ease: EASE }}
      className="relative pl-12 sm:pl-20 pb-14 last:pb-0"
    >
      <span className="absolute left-[14px] sm:left-[22px] top-2 w-3 h-3 -translate-x-1/2 rounded-full bg-acid ring-4 ring-ink" />
      <ItemBody item={item} />
    </motion.div>
  );
}

/* Horizontal timeline stop — dot on a rail running with the camera */
function TimelineStop({ item, index }) {
  const alt = index % 2 === 1;
  return (
    <motion.div
      initial={{ opacity: 0, x: 60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, delay: 0.08, ease: EASE }}
      className={`relative w-[34vw] shrink-0 flex flex-col ${alt ? "justify-start pt-[8vh]" : "justify-end pb-[8vh]"}`}
    >
      {/* connector to the rail */}
      <span
        className={`absolute left-1 w-px bg-paper/15 ${
          alt ? "top-1/2 h-[8vh] -translate-y-full" : "top-1/2 h-[8vh]"
        }`}
      />
      <span className="absolute left-1 top-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-acid ring-4 ring-ink" />
      <span className="absolute left-4 top-1/2 -translate-y-1/2 font-mono text-[10px] text-paper-dim">
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="pl-6 pr-10">
        <ItemBody item={item} />
      </div>
    </motion.div>
  );
}

function CertCard({ cert, index }) {
  return (
    <Reveal delay={index * 0.08}>
      <button
        onClick={() => window.open(cert.image, "_blank")}
        data-cursor-label="view"
        className="group block w-full text-left"
      >
        <div className="relative overflow-hidden border border-paper/10 bg-ink-soft aspect-[4/3]">
          <img
            src={cert.image}
            alt={cert.title}
            className="w-full h-full object-cover opacity-80 grayscale group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-expo"
          />
          <span className="absolute inset-0 ring-1 ring-inset ring-transparent group-hover:ring-acid transition-all duration-500" />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-paper group-hover:text-acid transition-colors">
            {cert.title}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-paper-dim">
            {cert.tag}
          </span>
        </div>
      </button>
    </Reveal>
  );
}

export default function Experience({ horizontal }) {
  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: lineRef,
    offset: ["start 25%", "end 80%"],
  });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  if (horizontal) {
    return (
      <section id="experience" className="flex h-full shrink-0">
        {/* Scene 1 — the title card */}
        <Panel horizontal className="flex flex-col justify-center px-6 sm:px-10">
          <SectionHeader />
          <Parallax horizontal depth={-0.05}>
            <MaskText
              className="display text-[7vw] leading-[1.02]"
              lineClassName="text-paper"
              lines={[<>The road <span className="italic text-acid">so far.</span></>]}
            />
          </Parallax>
          <Reveal delay={0.2}>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-ultra text-paper-dim">
              2010 — Now · Follow the line →
            </p>
          </Reveal>
        </Panel>

        {/* Scenes 2–3 — the timeline travels with the camera */}
        <Panel horizontal width={2} className="flex items-center">
          {/* the rail */}
          <span className="absolute left-0 right-0 top-1/2 h-px bg-paper/12" />
          <span className="absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-acid/60 via-acid/20 to-transparent" />
          <div className="relative flex items-stretch h-full w-full pl-[4vw]">
            {TIMELINE.map((item, i) => (
              <TimelineStop key={i} item={item} index={i} />
            ))}
          </div>
        </Panel>

        {/* Scene 4 — credentials */}
        <Panel horizontal className="flex flex-col justify-center px-10 xl:px-16">
          <div className="flex items-center gap-4 mb-10">
            <span className="eyebrow">Certifications</span>
            <span className="h-px flex-1 bg-paper/10" />
            <span className="eyebrow">04 Credentials</span>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
            {CERTS.map((c, i) => (
              <CertCard key={i} cert={c} index={i} />
            ))}
          </div>
        </Panel>
      </section>
    );
  }

  return (
    <section id="experience" className="relative py-24 sm:py-36 px-6 sm:px-10">
      <SectionHeader />

      <MaskText
        className="display text-[9vw] mdl:text-[5vw] leading-[1.02] mb-20"
        lineClassName="text-paper"
        lines={[<>The road <span className="italic text-acid">so far.</span></>]}
      />

      {/* timeline */}
      <div ref={lineRef} className="relative">
        {/* base rail */}
        <span className="absolute left-[14px] sm:left-[22px] top-0 bottom-0 w-px bg-paper/12" />
        {/* drawn progress rail */}
        <motion.span
          className="absolute left-[14px] sm:left-[22px] top-0 bottom-0 w-px bg-acid origin-top"
          style={{ scaleY }}
        />
        {TIMELINE.map((item, i) => (
          <TimelineItem key={i} item={item} index={i} />
        ))}
      </div>

      {/* achievements */}
      <div className="mt-28">
        <div className="flex items-center gap-4 mb-10">
          <span className="eyebrow">Certifications</span>
          <span className="h-px flex-1 bg-paper/10" />
          <span className="eyebrow">04 Credentials</span>
        </div>

        <div className="grid grid-cols-2 lgl:grid-cols-4 gap-4 sm:gap-6">
          {CERTS.map((c, i) => (
            <CertCard key={i} cert={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
